import { useMemo, useState } from 'react';
import type {
  ClientLocationsView,
  Location,
  LocationStore,
  PlatformKey,
  SuggestedLink,
} from '@clarity/contract';
import {
  useClientLocationsView,
  useClients,
  useCreateLocation,
  useDeleteLocation,
  useUpdateLocation,
} from '../../queries/index.js';
import { useI18n } from '../../lib/i18n.js';
import { platformLabels } from '../../lib/platformLabels.js';
import { Modal } from '../Modal.js';

/**
 * Admin "Locations" page (clarity-locations Req 4), reproducing the signed-off
 * mock `brand/locations-admin-mock.html`. A client picker, a Suggested-links
 * section, the Locations list (member stores + platform chips + ids + edit/
 * unlink), the Not-linked-yet pool (multi-select → group), and a create/edit
 * dialog (name EN + optional Arabic + member checkboxes). Bilingual + RTL via
 * `useI18n()`. A Location is pure grouping metadata; the server enforces admin.
 */

type Translate = ReturnType<typeof useI18n>['t'];

function platformName(p: PlatformKey): string {
  return platformLabels(p).platformLabel;
}

/** The server's `{ body: { message } }` error text, if any. */
function errorMessage(payload: unknown): string | undefined {
  if (payload && typeof payload === 'object' && 'body' in payload) {
    const body = (payload as { body?: { message?: unknown } }).body;
    if (body && typeof body.message === 'string' && body.message !== '') return body.message;
  }
  return undefined;
}

function PlatformChip({ p }: { p: PlatformKey }) {
  return (
    <span className={`plat ${p}`}>
      <span className="plat-dot" aria-hidden="true" />
      {platformName(p)}
    </span>
  );
}

function storeDisplayName(s: LocationStore): string {
  return s.name ?? s.externalStoreId;
}

export function LocationsAdmin() {
  const { t } = useI18n();
  const clientsQuery = useClients();
  const clients = clientsQuery.data?.status === 200 ? clientsQuery.data.body : [];
  const [clientId, setClientId] = useState<string>('');
  const activeClientId = clientId || clients[0]?.id || '';

  const viewQuery = useClientLocationsView(activeClientId, activeClientId !== '');
  const view: ClientLocationsView | null =
    viewQuery.data?.status === 200 ? viewQuery.data.body : null;

  const createMut = useCreateLocation(activeClientId);
  const updateMut = useUpdateLocation(activeClientId);
  const deleteMut = useDeleteLocation(activeClientId);

  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [editor, setEditor] = useState<
    | { mode: 'new'; seedName: string; seedIds: string[] }
    | { mode: 'edit'; location: Location }
    | null
  >(null);
  const [error, setError] = useState<string | null>(null);

  const storesById = useMemo(() => {
    const m = new Map<string, LocationStore>();
    for (const s of view?.stores ?? []) m.set(s.storeId, s);
    return m;
  }, [view]);

  const loose = (view?.stores ?? []).filter((s) => s.locationId === null);

  function togglePick(id: string) {
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function openNew(seedIds: string[], seedName = '') {
    setError(null);
    setEditor({ mode: 'new', seedName, seedIds });
  }
  function openEdit(location: Location) {
    setError(null);
    setEditor({ mode: 'edit', location });
  }

  async function unlink(location: Location, storeId: string) {
    const remaining = location.storeIds.filter((id) => id !== storeId);
    if (remaining.length === 0) {
      await deleteMut.mutateAsync({ params: { locationId: location.id } });
    } else {
      await updateMut.mutateAsync({ params: { locationId: location.id }, body: { storeIds: remaining } });
    }
  }

  async function removeLocation(location: Location) {
    await deleteMut.mutateAsync({ params: { locationId: location.id } });
  }

  if (!clientsQuery.isSuccess && clientsQuery.isLoading) {
    return (
      <section>
        <div className="page-head">
          <h2>{t('locations.title')}</h2>
        </div>
        <div className="card" role="status">
          {t('locations.loading')}…
        </div>
      </section>
    );
  }

  return (
    <section>
      <div className="page-head">
        <div>
          <h2>{t('locations.title')}</h2>
          <p className="sub">{t('locations.sub')}</p>
        </div>
        <div className="actions" style={{ display: 'flex', gap: 'var(--space-3)', alignItems: 'center' }}>
          <label className="sr-only" htmlFor="loc-client">
            {t('locations.client')}
          </label>
          <select
            id="loc-client"
            value={activeClientId}
            onChange={(e) => setClientId(e.target.value)}
          >
            {clients.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
          <button className="primary" type="button" disabled={loose.length === 0} onClick={() => openNew([])}>
            {t('locations.new')}
          </button>
        </div>
      </div>

      {viewQuery.isLoading && (
        <div className="card" role="status">
          {t('locations.loading')}…
        </div>
      )}

      {viewQuery.isError && (
        <div className="card danger" role="alert">
          <p>{t('locations.error.title')}</p>
          <button className="ghost" type="button" onClick={() => viewQuery.refetch()}>
            {t('locations.retry')}
          </button>
        </div>
      )}

      {view && (
        <>
          {/* Suggested links */}
          {view.suggestions.length > 0 && (
            <div className="card">
              <div className="page-head" style={{ marginBottom: 'var(--space-3)' }}>
                <h3>{t('locations.suggest.head')}</h3>
              </div>
              <ul className="loc-suggest-list">
                {view.suggestions.map((sg) => (
                  <SuggestionRow
                    key={sg.key}
                    sg={sg}
                    t={t}
                    onLink={() => openNew(sg.storeIds, sg.localityLabel)}
                  />
                ))}
              </ul>
            </div>
          )}

          {/* Locations */}
          <div className="card">
            <div className="page-head" style={{ marginBottom: 'var(--space-3)' }}>
              <h3>{t('locations.list.head')}</h3>
            </div>
            {view.locations.length === 0 ? (
              <p className="muted">{t('locations.pool.head')} ↓</p>
            ) : (
              view.locations.map((loc) => {
                const members = loc.storeIds
                  .map((id) => storesById.get(id))
                  .filter((s): s is LocationStore => s !== undefined);
                const platforms = [...new Set(members.map((m) => m.platformKey))];
                return (
                  <div className="loc-group" key={loc.id}>
                    <div className="loc-group-head">
                      <span className="loc-group-name">
                        {loc.name}
                        {loc.arabicName ? (
                          <bdi dir="rtl" lang="ar" className="muted">
                            {' '}
                            {loc.arabicName}
                          </bdi>
                        ) : null}
                      </span>
                      <span className="loc-group-pins">
                        {platforms.map((p) => (
                          <PlatformChip key={p} p={p} />
                        ))}
                      </span>
                      <button className="ghost sm" type="button" onClick={() => openEdit(loc)}>
                        {t('locations.list.edit')}
                      </button>
                      <button className="ghost sm danger" type="button" onClick={() => void removeLocation(loc)}>
                        {t('locations.toast.deleted')}
                      </button>
                    </div>
                    <ul className="loc-group-stores">
                      {members.map((m) => (
                        <li className="loc-store-row" key={m.storeId}>
                          <PlatformChip p={m.platformKey} />
                          <span className="loc-store-name">{storeDisplayName(m)}</span>
                          <span className="num muted">{m.externalStoreId}</span>
                          <button className="ghost sm" type="button" onClick={() => void unlink(loc, m.storeId)}>
                            {t('locations.list.unlink')}
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })
            )}
          </div>

          {/* Not linked yet */}
          <div className="card">
            <div className="page-head" style={{ marginBottom: 'var(--space-3)' }}>
              <h3>{t('locations.pool.head')}</h3>
            </div>
            {loose.length === 0 ? (
              <p className="muted">{t('locations.pool.empty')}</p>
            ) : (
              <ul className="loc-pool">
                {loose.map((s) => (
                  <li className="loc-pool-row" key={s.storeId} data-picked={picked.has(s.storeId)}>
                    <input
                      type="checkbox"
                      checked={picked.has(s.storeId)}
                      onChange={() => togglePick(s.storeId)}
                      aria-label={storeDisplayName(s)}
                    />
                    <PlatformChip p={s.platformKey} />
                    <span className="loc-pool-name">
                      <strong>{storeDisplayName(s)}</strong>
                      <span className="muted sm">
                        {' '}
                        · {platformName(s.platformKey)} · {t('locations.pool.outlet', { id: s.externalStoreId })}
                      </span>
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {picked.size > 0 && (
            <div className="loc-selbar">
              <span>{t('locations.sel.n', { count: picked.size })}</span>
              <span style={{ flex: 1 }} />
              <button className="ghost sm" type="button" onClick={() => setPicked(new Set())}>
                {t('locations.sel.clear')}
              </button>
              <button className="primary sm" type="button" onClick={() => openNew([...picked])}>
                {t('locations.sel.group')}
              </button>
            </div>
          )}
        </>
      )}

      {editor && view && (
        <LocationEditor
          t={t}
          editor={editor}
          stores={view.stores}
          error={error}
          onClose={() => setEditor(null)}
          onSave={async (payload) => {
            setError(null);
            try {
              if (editor.mode === 'new') {
                await createMut.mutateAsync({
                  params: { clientId: activeClientId },
                  body: { name: payload.name, arabicName: payload.arabicName, storeIds: payload.storeIds },
                });
              } else {
                await updateMut.mutateAsync({
                  params: { locationId: editor.location.id },
                  body: { name: payload.name, arabicName: payload.arabicName, storeIds: payload.storeIds },
                });
              }
              setPicked(new Set());
              setEditor(null);
            } catch (err) {
              const msg = errorMessage(err);
              setError(
                msg && /another location/i.test(msg) ? t('locations.err.conflict') : t('locations.err.generic'),
              );
            }
          }}
        />
      )}
    </section>
  );
}

function SuggestionRow({ sg, t, onLink }: { sg: SuggestedLink; t: Translate; onLink: () => void }) {
  const platforms = sg.platforms.map((p) => platformName(p)).join(' + ');
  return (
    <li className="loc-suggest">
      <div className="loc-suggest-txt">
        <strong>{sg.localityLabel}</strong>
        <span className="loc-suggest-pins">
          {sg.platforms.map((p) => (
            <PlatformChip key={p} p={p} />
          ))}
        </span>
        <div className="loc-suggest-why muted sm">
          {t('locations.suggest.why', { area: sg.localityLabel, platforms })}
        </div>
      </div>
      <button className="primary sm" type="button" onClick={onLink}>
        {t('locations.suggest.link')}
      </button>
    </li>
  );
}

interface EditorPayload {
  name: string;
  arabicName: string | null;
  storeIds: string[];
}

function LocationEditor({
  t,
  editor,
  stores,
  error,
  onClose,
  onSave,
}: {
  t: Translate;
  editor: { mode: 'new'; seedName: string; seedIds: string[] } | { mode: 'edit'; location: Location };
  stores: LocationStore[];
  error: string | null;
  onClose: () => void;
  onSave: (payload: EditorPayload) => void | Promise<void>;
}) {
  const initialName = editor.mode === 'new' ? editor.seedName : editor.location.name;
  const initialAr = editor.mode === 'new' ? '' : editor.location.arabicName ?? '';
  const initialMembers = editor.mode === 'new' ? editor.seedIds : editor.location.storeIds;

  const [name, setName] = useState(initialName);
  const [ar, setAr] = useState(initialAr);
  const [members, setMembers] = useState<Set<string>>(new Set(initialMembers));

  const currentLocationId = editor.mode === 'edit' ? editor.location.id : null;
  // Candidate stores: loose, plus this location's own members when editing.
  const candidates = stores.filter(
    (s) => s.locationId === null || (currentLocationId !== null && s.locationId === currentLocationId),
  );

  function toggle(id: string) {
    setMembers((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  const title = editor.mode === 'new' ? t('locations.editor.titleNew') : t('locations.editor.titleEdit');

  return (
    <Modal title={title} onClose={onClose}>
      <p className="muted sm">{t('locations.editor.sub')}</p>
      {error && (
        <div className="danger sm" role="alert" style={{ marginBlock: 'var(--space-2)' }}>
          {error}
        </div>
      )}
      <div className="field">
        <label htmlFor="loc-name">{t('locations.editor.name')}</label>
        <input id="loc-name" type="text" value={name} onChange={(e) => setName(e.target.value)} autoFocus />
      </div>
      <div className="field">
        <label htmlFor="loc-ar">{t('locations.editor.ar')}</label>
        <input id="loc-ar" type="text" dir="rtl" value={ar} onChange={(e) => setAr(e.target.value)} />
      </div>
      <div className="field">
        <label>{t('locations.editor.members')}</label>
        <div className="loc-member-list">
          {candidates.map((s) => (
            <label className="loc-member" key={s.storeId}>
              <input type="checkbox" checked={members.has(s.storeId)} onChange={() => toggle(s.storeId)} />
              <PlatformChip p={s.platformKey} />
              <span>
                {storeDisplayName(s)} <span className="num muted">{s.externalStoreId}</span>
              </span>
            </label>
          ))}
        </div>
      </div>
      <div className="modal-actions">
        <button className="ghost" type="button" onClick={onClose}>
          {t('locations.editor.cancel')}
        </button>
        <button
          className="primary"
          type="button"
          disabled={name.trim() === '' || members.size === 0}
          onClick={() =>
            void onSave({ name: name.trim(), arabicName: ar.trim() === '' ? null : ar.trim(), storeIds: [...members] })
          }
        >
          {t('locations.editor.save')}
        </button>
      </div>
    </Modal>
  );
}
